/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement } from "react";

import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import * as Yup from "yup";

import { Stack, StackItem } from "@fluentui/react";
import { yupResolver } from "@hookform/resolvers/yup";

import RadioSelectController from "Components/ReactHookForm/RadioSelectController/RadioSelectController";
import TextFieldController from "Components/ReactHookForm/TextFieldController/TextFieldController";
import { SidePanel } from "Components/SidePanel/SidePanel";
import { OperationStatus } from "Constants/OperationStatus";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { scoreApplication, scoreApplicationReset } from "Dataflows/Application/ApplicationAction";
import {
  getScoreApplicationErrorMessage,
  getScoreApplicationStatus,
} from "Dataflows/Application/ApplicationSelector";

import { IApplicationScoringSidePanelProps } from "./IApplicationScoringSidePanel";

export const ApplicationScoringSidePanel = (
  props: IApplicationScoringSidePanelProps
): ReactElement => {
  const { isPanelOpen, setIsPanelOpen, technologyAreaName, applicationFormId, onSubmitSuccess } =
    props;
  const dispatch = useDispatch();

  const scoreApplicationErrorMessage = useSelector(getScoreApplicationErrorMessage);
  const scoreApplicationStatus = useSelector(getScoreApplicationStatus);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        score: Yup.number().typeError("Score must be a number").required("Score is required"),
        justification: Yup.string().required("Justification is required").max(3900),
      })
    ),
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const save = () =>
    handleSubmit((formData) => {
      dispatch(
        scoreApplication(
          applicationFormId,
          technologyAreaName,
          formData.score,
          formData.justification,
          TenantTypesEnum.MVP
        )
      );
    });

  const dismissPanel = (): void => {
    setIsPanelOpen(false);
    dispatch(scoreApplicationReset());
    reset();
  };

  useEffect(() => {
    if (scoreApplicationStatus === OperationStatus.Succeeded) {
      dismissPanel();
      onSubmitSuccess();
    }
  }, [scoreApplicationStatus]);

  const scoreOptions = [
    {
      key: "1",
      text: "1: Candidate's technical expertise is far below minimum program requirements.",
    },
    {
      key: "2",
      text: "2: Candidate's technical expertise does not meet the program requirements.",
    },
    {
      key: "3",
      text: "3: Candidate's technical expertise meets the program requirements.",
    },
    {
      key: "4",
      text: "4: Candidate's technical expertise is above program requirements.",
    },
    {
      key: "5",
      text: "5: Candidate is highly technical, far above program requirements.",
    },
  ];

  return (
    <form onSubmit={save()}>
      <SidePanel
        isOpen={isPanelOpen}
        title={`Review ${technologyAreaName}`}
        saveButtonLabel="Save"
        error={scoreApplicationErrorMessage}
        disableActions={scoreApplicationStatus === OperationStatus.Pending}
        onDismiss={dismissPanel}
        onSave={save()}
      >
        <Stack tokens={{ childrenGap: "1rem" }}>
          <StackItem>
            <RadioSelectController
              inputName="score"
              control={control}
              errors={errors}
              isRequired
              labelDisplayName="Score"
              options={scoreOptions}
            />
          </StackItem>
          <StackItem>
            <TextFieldController
              inputName="justification"
              control={control}
              errors={errors}
              isRequired
              isMultiline
              hasTooltip
              labelDisplayName="Justification"
              toolTipDisplayText="Please let us know why you chose this score for the candidate."
              characterCountLimit={3900}
            />
          </StackItem>
        </Stack>
      </SidePanel>
    </form>
  );
};
